/**
 * 判断是否是字符串
 * @param str
 * @returns {boolean}
 */
export function isString(str) {
  return typeof str === "string" || str instanceof String;
}

/**
 * 判断是否是数组
 * @param arg
 * @returns {arg is any[]|boolean}
 */
export function isArray(arg) {
  if (typeof Array.isArray === "undefined") {
    return Object.prototype.toString.call(arg) === "[object Array]";
  }
  return Array.isArray(arg);
}

/**
 * 判断是否是方法
 * @param fun
 * @returns boolean
 */
export function isFunction(fun) {
  return typeof fun === "function";
}

/**
 * 判断是否是布尔值
 * @param arg
 * @returns boolean
 */
export function isBoolean(arg) {
  return typeof arg === "boolean";
}

/**
 * 判断是否为空
 * @param arg
 * @returns boolean
 */
export function isNull(arg) {
  return arg === null || arg === undefined;
}

/**
 * 判断是否不为空
 * @param arg
 * @returns boolean
 */
export function isNotNull(arg) {
  return arg !== null && arg !== undefined;
}

/**
 * 判断是否为普通对象
 * @param arg
 * @returns boolean
 */
export function isPlainObject(arg) {
  return Object.prototype.toString.call(arg) === "[object Object]";
}

/**
 * 判断两个对象是否相等，值属性相同，而不是引用地址相同
 * @param obj1
 * @param obj2
 * @returns boolean
 */
export function objEqual(obj1, obj2) {
  // 首先检查两个对象是否都是对象类型
  if (
    typeof obj1 !== "object" ||
    obj1 === null ||
    typeof obj2 !== "object" ||
    obj2 === null
  ) {
    return obj1 === obj2; // 如果不是对象，直接比较
  }

  // 获取两个对象的属性名数组
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  // 检查属性数量是否相同
  if (keys1.length !== keys2.length) {
    return false;
  }

  // 递归比较每个属性
  for (let key of keys1) {
    if (!keys2.includes(key) || !objEqual(obj1[key], obj2[key])) {
      return false;
    }
  }

  return true; // 如果所有属性都相同，返回true
}

/**
 * 深度拷贝
 * @param arg
 * @returns obj
 */
export function deepCopy(arg) {
  return JSON.parse(JSON.stringify(arg));
}

/**
 * 防抖函数处理
 * @param func 函数
 * @param delay 延时时间（毫秒）
 * @returns function
 */
export function debounce(func, delay = 200) {
  let timer = null;
  return function () {
    const context = this;
    const args = arguments;
    clearTimeout(timer);
    timer = setTimeout(function () {
      func.apply(context, args);
    }, delay);
  };
}

/**
 * 节流函数处理
 * @param func 函数
 * @param delay 间隔时间（毫秒）
 * @returns function
 */
export function throttle(func, delay = 200) {
  let timer = null;
  let lastCall = 0;

  return function () {
    const context = this;
    const args = arguments;
    const now = Date.now();
    if (now - lastCall < delay) {
      clearTimeout(timer);
      timer = setTimeout(() => {
        lastCall = now;
        func.apply(context, args);
      }, delay);
    } else {
      lastCall = now;
      func.apply(context, args);
    }
  };
}

/**
 * 处理路由页面切换时，异步组件加载过渡的处理函数
 * @param  {Object} AsyncView 需要加载的组件，如 import('@/components/home/Home.vue')
 * @param loadingComponent 加载中遮罩组件
 * @return {Object} 返回一个promise对象
 */
export function lazyLoadView(
  AsyncView,
  loadingComponent = require("@/components/layouts/LoadingSkeleton.vue").default
) {
  const AsyncHandler = () => ({
    // 需要加载的组件 (应该是一个 `Promise` 对象)
    component: AsyncView,
    // 异步组件加载时使用的组件
    loading: loadingComponent,
    // 加载失败时使用的组件
    // error: require('@/components/layouts/LoadingComponent.vue').default,
    // 展示加载时组件的延时时间。默认值是 200 (毫秒)
    delay: 150,
    // 如果提供了超时时间且组件加载也超时了，
    // 则使用加载失败时使用的组件。默认值是：`Infinity`
    // timeout: Infinity
  });
  return Promise.resolve({
    functional: true,
    render(h, { data, children }) {
      return h(AsyncHandler, data, children);
    },
  });
}
